import { useEffect, useState } from "react";
import { Download, X, Share2 } from "lucide-react";

const PROMPT_DISMISSED_KEY = "scintilla_pwa_prompt_dismissed_until";
const PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isiOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const iPadOS13 = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    return iOS || iPadOS13;
}

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [showIOSTip, setShowIOSTip] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;

        const dismissedUntil = parseInt(
            localStorage.getItem(PROMPT_DISMISSED_KEY) || "0",
            10,
        );
        if (Date.now() < dismissedUntil) return;

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
            setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        // iOS fallback: show tip after 3s if on iOS and not installed
        let t;
        if (isiOS()) {
            t = setTimeout(() => {
                setVisible(true);
                setShowIOSTip(true);
            }, 3000);
        }

        const onInstalled = () => {
            setVisible(false);
            localStorage.removeItem(PROMPT_DISMISSED_KEY);
        };
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
            if (t) clearTimeout(t);
        };
    }, []);

    const dismiss = () => {
        localStorage.setItem(
            PROMPT_DISMISSED_KEY,
            String(Date.now() + PROMPT_COOLDOWN_MS),
        );
        setVisible(false);
    };

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        const choice = await deferred.userChoice;
        setDeferred(null);
        setVisible(false);
        if (choice.outcome !== "accepted") {
            localStorage.setItem(
                PROMPT_DISMISSED_KEY,
                String(Date.now() + PROMPT_COOLDOWN_MS),
            );
        }
    };

    if (!visible) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-[#0C4650] text-white rounded-2xl shadow-2xl z-50 overflow-hidden fade-up"
            data-testid="pwa-install-prompt"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)" }}
        >
            <div className="relative p-5">
                <button
                    onClick={dismiss}
                    className="absolute top-3 right-3 text-white/50 hover:text-white"
                    data-testid="pwa-dismiss"
                    aria-label="Chiudi"
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#C7FF2C] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#C7FF2C]/30">
                        <Download className="w-5 h-5 text-[#0C4650]" strokeWidth={3} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.3em] text-[#C7FF2C] font-black">
                            SCINTILLA HUB
                        </div>
                        <div className="font-black text-sm">Installa l'app sul tuo dispositivo</div>
                    </div>
                </div>

                {showIOSTip ? (
                    <div className="mt-4 text-sm text-white/80 space-y-2">
                        <p>Per installare su iPhone/iPad:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                            <li>
                                Tocca{" "}
                                <span className="inline-flex items-center gap-1 bg-white/10 px-1.5 py-0.5 rounded">
                                    <Share2 className="w-3 h-3" /> Condividi
                                </span>{" "}
                                in basso nel browser
                            </li>
                            <li>Scorri e scegli "Aggiungi a Home"</li>
                            <li>Tocca "Aggiungi" in alto a destra</li>
                        </ol>
                        <button
                            onClick={dismiss}
                            className="mt-2 w-full btn-primary !py-2 !text-xs"
                            data-testid="pwa-ios-ok"
                        >
                            Capito
                        </button>
                    </div>
                ) : (
                    <div className="mt-4 flex gap-2">
                        <button
                            onClick={install}
                            className="flex-1 bg-[#C7FF2C] text-[#0C4650] font-bold py-2.5 rounded-lg hover:bg-white transition-colors text-sm inline-flex items-center justify-center gap-2"
                            data-testid="pwa-install-btn"
                        >
                            <Download className="w-4 h-4" /> Installa
                        </button>
                        <button
                            onClick={dismiss}
                            className="px-4 py-2.5 text-white/70 hover:text-white font-bold text-sm"
                            data-testid="pwa-later"
                        >
                            Più tardi
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
